import * as React from 'react'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'

import Grid from '@mui/material/Grid'

import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Dialog from '@mui/material/Dialog'
import LoadingButton from '@mui/lab/LoadingButton'

import { renderImportOptionsMenu } from './SubImportMenuItem'

import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import StepButton from '@mui/material/StepButton'

import Typography from '@mui/material/Typography'

import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined'
import MultipleSelect from '../MultiSelect'
import EditableSelect from '../EditableSelect'
import MultipleTextInput from '../MultiValueTextInput'
import TextField from '@mui/material/TextField'

import {
    useContextsStore,
    selectOwnedContexts,
} from '../../stores/useContextsStore'
import { UserContext } from '../../UserContext'

import { useTheme } from '@mui/material/styles'

import { SubImportContext } from './SubImportContext'
import { DialogsContext } from '../../DialogsContext'

import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import InputLabel from '@mui/material/InputLabel'

import { format } from 'date-fns'

import axios from 'axios'
import qs from 'qs'

import { slugifyString, splitUrlsIntoArray } from '../../utils/index'

import { generateTextImportItems } from './TextImportItems'
import AdvancedImportSettings from './AdvancedImportSettings'
import ImportingNotice from './ImportingNotice'
import { useSimplifiedImportAtMount } from '../../hooks/useSimplifiedImportAtMount'
import { importErrorMessage } from './importError'

const steps = ['Start', 'Sources', 'Settings', 'Results']

export default function URLsImportDialog({
    presetContext = '',
    presetSearchString = '',
    open,
    onClose,
}) {
    const theme = useTheme() // hook to get the current theme

    const {
        activeSubImportDialog,
        setActiveSubImportDialog,
    } = React.useContext(SubImportContext)

    const {
        activeImportDialog,
        setActiveImportDialog,
        backLinkDialog,
        openImportDialog,
        closeImportDialog,
    } = React.useContext(DialogsContext)

    const [selectedContext, setSelectedContext] = React.useState(presetContext)

    const [errorMessage, setErrorMessage] = React.useState(null)

    const [importError, setImportError] = React.useState(null)

    const [presetUrls, setPresetUrls] = React.useState([])

    const [selectedUrls, setSelectedUrls] = React.useState([])

    React.useEffect(() => {
        setPresetUrls(
            presetSearchString ? splitUrlsIntoArray(presetSearchString) : []
        )
    }, [presetSearchString])

    React.useEffect(() => {
        setSelectedUrls(presetUrls)
    }, [presetUrls])

    const [analyzeHeadersOnly, setAnalyzeHeadersOnly] = React.useState(false)

    const [completedSteps, setCompletedSteps] = React.useState([])

    const [visitedContext, setVisitedContext] = React.useState(false)

    const [visitedFeeds, setVisitedFeeds] = React.useState(false)

    const [searchString, setSearchString] = React.useState('')

    const [useAsCategories, setUseAsCategories] = React.useState('0')

    const [modifyAnalyzedText, setModifyAnalyzedText] = React.useState('none')

    const [replaceEntities, setReplaceEntities] = React.useState(false)

    const [analyzeWebsite, setAnalyzeWebsite] = React.useState(false)

    const [numberOfPages, setNumberOfPages] = React.useState('10')

    const [crawlSitemap, setCrawlSitemap] = React.useState(false)

    const handleNumberOfPagesChange = event => {
        setNumberOfPages(event.target.value)
    }

    const menuItems = generateTextImportItems()

    const filteredMenuItems = menuItems.filter(
        ({ textInput }) =>
            textInput.includes('urls') || textInput.includes(backLinkDialog)
    )

    const { username } = React.useContext(UserContext)
    const contexts = useContextsStore(selectOwnedContexts)
    const contextNames = contexts.map(c => c.name)

    const simplifyImport = useSimplifiedImportAtMount()

    const [activeStep, setActiveStep] = React.useState(2)
    const [skipped, setSkipped] = React.useState(new Set())

    const handleNext = () => {
        let newSkipped = skipped

        setActiveStep(prevActiveStep => prevActiveStep + 1)
        setSkipped(newSkipped)
    }

    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1)
    }

    const handleStep = step => {
        setActiveStep(step)
    }

    const handleUseAsCategoriesChange = event => {
        setUseAsCategories(event.target.value)
    }

    React.useEffect(() => {
        if (activeSubImportDialog.type) {
            setActiveStep(2)
            setActiveImportDialog(activeSubImportDialog.type)
        }
    }, [activeSubImportDialog])

    React.useEffect(() => {
        setImportError(null)
        if (selectedUrls.length > 1) {
            setAnalyzeWebsite(false)
        }
    }, [selectedUrls])

    React.useEffect(() => {
        switch (activeStep) {
            case 0: // Start
                setActiveSubImportDialog(false)
                setActiveImportDialog('import')
                break
            case 1: // Sources
                setActiveSubImportDialog(false)
                setActiveImportDialog('linkImport')
                break
            case 2: // Settings
                if (!presetContext && !selectedContext) {
                    setSelectedContext(generatePresetContext())
                }
                break
            case 3: // Analysis
                if (!selectedUrls || selectedUrls.length == 0) {
                    setErrorMessage(
                        `Please, provide at least one URL link or website you'd like to analyze.`
                    )
                    setActiveStep(2)
                    break
                }
                if (!selectedContext) {
                    setErrorMessage(
                        'Please, select the graph to import your data to.'
                    )
                    setActiveStep(2)
                    break
                }
                if (selectedContext) {
                    setCompletedSteps([...completedSteps, 3])
                    setErrorMessage(null)
                    submitDataForm()
                    break
                }
        }
    }, [activeStep])

    const submitDataForm = () => {
        const urlInputString = selectedUrls.join('\r\n')

        const urlToSubmit = analyzeWebsite ? '/import/website' : '/import/url'

        const dataToSend = {
            urls: urlInputString,
            fieldsToProcess: '', // TODO add to settings
            analyzeHeadersOnly: analyzeHeadersOnly ? 'true' : '',
            context: selectedContext,
            selectedContexts: '',
            statementid: '',
            modifyAnalyzedText: modifyAnalyzedText,
            replaceEntities: replaceEntities,
            contentToAnalyze: analyzeWebsite ? 'website' : 'urls',
            limit: numberOfPages ? numberOfPages : '10',
            searchQuery: '', // TODO add ability to extract content that contains certain ideas only
            crawlSitemap: crawlSitemap ? 'true' : '',
        }

        const dataAsForm = qs.stringify(dataToSend)

        axios
            .post(urlToSubmit, dataAsForm, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded', // Sending as URL-encoded form data
                },
            })
            .then(response => {
                if (response.data.redirectUrl) {
                    // If you're using window.location
                    window.location.href = response.data.redirectUrl

                    // Or, if you're using React Router's history (assuming you have access to the history object)
                    // history.push(response.data.redirectUrl);
                } else {
                    console.error(`Did't receive any response from the server.`)
                    if (response.data.error) {
                        setErrorMessage(
                            typeof response.data.error == 'object'
                                ? response.data.error.error ||
                                      JSON.stringify(response.data.error)
                                : response.data.error
                        )
                        setImportError(response.data.error)
                        setActiveStep(2)
                    }
                }
            })
            .catch(error => {
                // Handle the error here
                console.error('Received the following error', error)
                setErrorMessage(importErrorMessage(error))
                setImportError(response.data.error)
            })
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth={true}
                maxWidth="lg"
            >
                <DialogTitle
                    sx={{
                        padding: 4,
                        color: theme => theme.palette.text.primary,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {'Analyze URLs, web pages, and websites'}
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                {/* A stepper whose labels submit on click would be the most prominent control left. */}
                {!simplifyImport && (
                    <Stepper
                        activeStep={activeStep}
                        sx={{ paddingLeft: 4, paddingRight: 4, paddingTop: 2 }}
                    >
                        {steps.map((label, index) => {
                            const stepProps = {}
                            const labelProps = {}

                            return (
                                <Step key={label} {...stepProps}>
                                    <StepButton
                                        onClick={() => handleStep(index)}
                                        completed={(
                                            completedSteps.indexOf(index) >= 0
                                        ).toString()}
                                    >
                                        <StepLabel
                                            {...labelProps}
                                            sx={{
                                                '& .MuiStepLabel-label': {
                                                    fontSize: {
                                                        xs: '0.75rem',
                                                        sm: 'initial',
                                                    },
                                                },
                                            }}
                                        >
                                            {label}
                                        </StepLabel>
                                    </StepButton>
                                </Step>
                            )
                        })}
                    </Stepper>
                )}

                {errorMessage && (
                    <Typography
                        sx={{
                            paddingRight: 4,
                            paddingLeft: 4,
                            paddingTop: 4,
                            paddingBottom: 0,
                        }}
                        color="error"
                        component="div"
                        gutterBottom
                    >
                        {errorMessage}
                    </Typography>
                )}

                {activeStep === 2 && (
                    <>
                        <DialogContent
                            sx={{
                                paddingRight: 4,
                                paddingLeft: 4,
                                paddingTop: 4,
                            }}
                        >
                            <Grid container spacing={8}>
                                <Grid item xs={12} sm={simplifyImport ? 12 : 7}>
                                    <DialogContentText>
                                        Create a text network visualization of
                                        one or several URLs:
                                    </DialogContentText>
                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 4 }}
                                    >
                                        <MultipleTextInput
                                            label={'URLs to analyze'}
                                            initialValues={presetUrls}
                                            setSelectedValues={setSelectedUrls}
                                            placeholder={
                                                'add some URLs and press enter...'
                                            }
                                            suggestions={[
                                                'https://infranodus.com',
                                                'https://nvidia.com',
                                                'https://www.hubermanlab.com/',
                                            ]}
                                        />
                                        <FormHelperText sx={{ marginTop: 2 }}>
                                            The URLs should start with http or
                                            https and if you add several,
                                            please, separate them with a space
                                            or a new line.
                                        </FormHelperText>
                                    </FormControl>

                                    <FormControl
                                        fullWidth
                                        sx={{ marginTop: 6 }}
                                    >
                                        <DialogContentText>
                                            Using the import app:
                                        </DialogContentText>
                                        <DialogContent
                                            sx={{
                                                pl: 0,
                                                ml: 0,
                                                marginTop: -2,
                                            }}
                                        >
                                            {renderImportOptionsMenu(
                                                filteredMenuItems,
                                                'urls'
                                            )}
                                        </DialogContent>
                                    </FormControl>
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    sm={simplifyImport ? 12 : 4}
                                    sx={{
                                        marginTop: 3,
                                        marginBottom: 2,
                                    }}
                                >
                                    <AdvancedImportSettings>
                                        <FormControl
                                            fullWidth
                                            sx={{
                                                marginTop: 4,
                                                marginBottom: 2,
                                            }}
                                        >
                                            <EditableSelect
                                                selectItems={contextNames}
                                                label={'Graph name'}
                                                initialValue={selectedContext}
                                                onChange={name =>
                                                    setSelectedContext(name)
                                                }
                                            />
                                        </FormControl>
                                        <FormControl
                                            fullWidth
                                            sx={{
                                                marginTop: 4,
                                                marginBottom: 2,
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={
                                                            analyzeHeadersOnly
                                                        }
                                                        onChange={event =>
                                                            setAnalyzeHeadersOnly(
                                                                event.target
                                                                    .checked
                                                            )
                                                        }
                                                    />
                                                }
                                                label="Analyze headers only"
                                            />

                                            <FormHelperText>
                                                extract only
                                                {' <h1> <h2> <h3> <h4> '} tags
                                                content
                                            </FormHelperText>
                                        </FormControl>
                                        <FormControl
                                            fullWidth
                                            sx={{
                                                marginTop: 4,
                                                marginBottom: 2,
                                            }}
                                        >
                                            <InputLabel id="useAsCategories-label">
                                                Use as categories
                                            </InputLabel>
                                            <Select
                                                labelId="useAsCategories-label"
                                                id="useAsCategories"
                                                value={useAsCategories}
                                                onChange={
                                                    handleUseAsCategoriesChange
                                                }
                                                label="Use as Categories"
                                            >
                                                <MenuItem value={'0'}>
                                                    URL Domains
                                                </MenuItem>
                                                <MenuItem value={'1'}>
                                                    Page Titles
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                        <FormControl
                                            fullWidth
                                            sx={{
                                                marginTop: 4,
                                                marginBottom: 2,
                                            }}
                                        >
                                            <InputLabel id="modifyAnalyzedText-label">
                                                Build a knowledge graph from
                                            </InputLabel>
                                            <Select
                                                labelId="modifyAnalyzedText-label"
                                                id="modifyAnalyzedText"
                                                value={modifyAnalyzedText}
                                                onChange={event =>
                                                    setModifyAnalyzedText(
                                                        event.target.value
                                                    )
                                                }
                                                label="Build a knowledge graph from"
                                            >
                                                <MenuItem
                                                    value={'none'}
                                                    key="1"
                                                >
                                                    the words used in text
                                                </MenuItem>
                                                <MenuItem
                                                    value={'detectEntities'}
                                                    key="2"
                                                >
                                                    the words and detected
                                                    entities
                                                </MenuItem>
                                                <MenuItem
                                                    value={
                                                        'extractEntitiesOnly'
                                                    }
                                                    key="3"
                                                >
                                                    only the detected entities
                                                </MenuItem>
                                            </Select>
                                            {modifyAnalyzedText != 'none' && (
                                                <FormHelperText
                                                    sx={{ marginTop: 1 }}
                                                >
                                                    By default, we extract the
                                                    words used in your text. You
                                                    can also choose to detect
                                                    and tag entities in your
                                                    text and build a graph from
                                                    them.
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                        {modifyAnalyzedText != 'none' && (
                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 2,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            id="replaceEntities"
                                                            checked={
                                                                replaceEntities
                                                            }
                                                            onChange={event =>
                                                                setReplaceEntities(
                                                                    event.target
                                                                        .checked
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Replace entities with root concepts"
                                                />
                                                {replaceEntities && (
                                                    <FormHelperText
                                                        id="replaceEntities-helperText"
                                                        sx={{ marginTop: 1 }}
                                                    >
                                                        When turned on, we will
                                                        not only detect the
                                                        entities, but also
                                                        replace them with their
                                                        ontological root
                                                        concepts throughout your
                                                        text.
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                        )}
                                        <FormControl
                                            fullWidth
                                            sx={{
                                                marginTop: 4,
                                                marginBottom: 2,
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={analyzeWebsite}
                                                        onChange={event =>
                                                            setAnalyzeWebsite(
                                                                event.target
                                                                    .checked
                                                            )
                                                        }
                                                        disabled={
                                                            selectedUrls.length >
                                                            1
                                                        }
                                                    />
                                                }
                                                label="Analyze the entire website"
                                            />

                                            {selectedUrls.length == 1 && (
                                                <FormHelperText>
                                                    crawl the entire website and
                                                    analyze its content
                                                </FormHelperText>
                                            )}
                                            {(selectedUrls.length == 0 ||
                                                selectedUrls.length > 1) && (
                                                <FormHelperText>
                                                    please, specify a single URL
                                                    to activate this feature
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                        {analyzeWebsite && (
                                            <>
                                                <FormControl
                                                    fullWidth
                                                    sx={{
                                                        marginTop: 4,
                                                        marginBottom: 2,
                                                    }}
                                                >
                                                    <InputLabel id="numberOfPages-label">
                                                        Number of pages to crawl
                                                    </InputLabel>
                                                    <Select
                                                        labelId="numberOfPages-label"
                                                        id="numberOfPages"
                                                        value={numberOfPages}
                                                        onChange={
                                                            handleNumberOfPagesChange
                                                        }
                                                        label="Number of pages to crawl"
                                                    >
                                                        <MenuItem value={'10'}>
                                                            10 pages
                                                        </MenuItem>
                                                        <MenuItem value={'20'}>
                                                            20 pages
                                                        </MenuItem>
                                                        <MenuItem value={'30'}>
                                                            30 pages
                                                        </MenuItem>
                                                        <MenuItem value={'40'}>
                                                            40 pages
                                                        </MenuItem>
                                                        <MenuItem value={'50'}>
                                                            50 pages
                                                        </MenuItem>
                                                        {/* <MenuItem value={'60'}>
                                                            60 pages
                                                        </MenuItem>
                                                        <MenuItem value={'80'}>
                                                            80 pages
                                                        </MenuItem>
                                                        <MenuItem value={'100'}>
                                                            100 pages
                                                        </MenuItem>
                                                        <MenuItem value={'150'}>
                                                            150 pages
                                                        </MenuItem>
                                                        <MenuItem value={'200'}>
                                                            200 pages
                                                        </MenuItem> */}
                                                    </Select>
                                                </FormControl>
                                                <FormControl
                                                    fullWidth
                                                    sx={{
                                                        marginTop: 4,
                                                        marginBottom: 2,
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={
                                                                    crawlSitemap
                                                                }
                                                                onChange={event =>
                                                                    setCrawlSitemap(
                                                                        event
                                                                            .target
                                                                            .checked
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label="Crawl the sitemap"
                                                    />

                                                    {crawlSitemap && (
                                                        <FormHelperText>
                                                            when on, we will
                                                            extract the pages to
                                                            crawl from the
                                                            website's sitemap,
                                                            from top to bottom
                                                        </FormHelperText>
                                                    )}
                                                    {!crawlSitemap && (
                                                        <FormHelperText>
                                                            when off, we will
                                                            load the website's
                                                            page and extract the
                                                            links to crawl from
                                                            it
                                                        </FormHelperText>
                                                    )}
                                                </FormControl>
                                            </>
                                        )}
                                    </AdvancedImportSettings>
                                </Grid>
                            </Grid>
                        </DialogContent>
                    </>
                )}

                {activeStep === 3 && (
                    <ImportingNotice
                        linkHref={`/${username}/${selectedContext}/edit`}
                        message="We are importing your data. Please, wait until this page reloads. If you are importing a whole website, this may take longer, but when the job is complete, it will appear in this graph:"
                        linkLabel={selectedContext}
                    />
                )}
                <DialogActions
                    sx={{
                        paddingRight: 4,
                        paddingLeft: 4,
                        paddingBottom: 4,
                        paddingTop: 2,
                    }}
                >
                    {/* <Button onClick={onClose} variant="outlined">
                        Cancel
                    </Button> */}
                    <Button
                        color="inherit"
                        variant="outlined"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{ mr: 1, px: 3, py: 1, fontSize: '0.95rem' }}
                    >
                        Back
                    </Button>
                    {/* {isStepOptional(activeStep) && (
                        <Button
                            color="inherit"
                            onClick={handleSkip}
                            sx={{ mr: 1 }}
                        >
                            Skip
                        </Button>
                    )} */}
                    <LoadingButton
                        color="info"
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        disabled={!selectedUrls.length > 0 || activeStep == 3}
                        onClick={handleNext}
                        sx={{ px: 4, py: 1, fontSize: '0.95rem' }}
                    >
                        {simplifyImport && activeStep === 2
                            ? 'Start processing'
                            : activeStep === 2
                            ? 'Visualize'
                            : 'Next'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    )
}

function generatePresetContext(type) {
    const dateAndTime = new Date()
        .toISOString()
        .slice(2, 16)
        .replace(/-/g, '')
        .replace(/:/g, '')

    if (!type) return `url_${dateAndTime}`

    return `${type}_${dateAndTime}`
}
